/**
 * Utility functions for generating PDF filenames
 */

/**
 * Sanitizes a string to be safe for use in filenames
 * Removes or replaces characters that are invalid in file systems
 */
function sanitizeFilename(name: string): string {
  if (!name) return 'Project';

  return (
    name
      // Remove invalid filename characters: < > : " / \ | ? *
      .replace(/[<>:"/\\|?*]/g, '')
      // Replace multiple spaces with single underscore
      .replace(/\s+/g, '_')
      // Remove leading/trailing underscores
      .replace(/^_+|_+$/g, '')
      // Limit length to prevent filesystem issues
      .substring(0, 50) || 'Project'
  );
}

/**
 * Formats a date for PDF filename in the format: YYYY-MM-DD-H:MMam/pm
 * Example: 2026-02-23-2:34pm
 */
function formatDateForFilename(date: Date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  // Get hour in 12-hour format without leading zero
  let hour = date.getHours();
  const ampm = hour >= 12 ? 'pm' : 'am';
  hour = hour % 12;
  hour = hour ? hour : 12; // Convert 0 to 12

  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day}-${hour}:${minutes}${ampm}`;
}

/**
 * Generates a PDF filename for Bill of Materials export
 * Format: ProjectName_ViewMode_YYYY-MM-DD-H:MMam/pm.pdf
 * Example: MyProject_Full_2026-02-23-2:34pm.pdf
 */
export function generateBillOfMaterialsPdfFilename(
  projectName: string,
  viewMode: 'full' | 'detailed',
): string {
  const sanitizedName = sanitizeFilename(projectName);
  const capitalizedViewMode = viewMode === 'full' ? 'Full' : 'Detailed';
  const dateString = formatDateForFilename();

  return `${sanitizedName}_${capitalizedViewMode}_${dateString}.pdf`;
}

/**
 * Generates a PDF filename for Material Catalog export
 * Format: Material_Catalog_YYYY-MM-DD-H:MMam/pm.pdf
 */
export function generateMaterialCatalogPdfFilename(): string {
  const dateString = formatDateForFilename();
  return `Material_Catalog_${dateString}.pdf`;
}

/**
 * Generates a PDF filename for Equipment Assets export
 * Format: Equipment_Assets_YYYY-MM-DD-H:MMam/pm.pdf
 */
export function generateEquipmentAssetsPdfFilename(): string {
  const dateString = formatDateForFilename();
  return `Equipment_Assets_${dateString}.pdf`;
}

/**
 * Generates a PDF filename for Stock Overview export
 * Format: Stock_Overview_YYYY-MM-DD-H:MMam/pm.pdf
 */
export function generateStockOverviewPdfFilename(): string {
  const dateString = formatDateForFilename();
  return `Stock_Overview_${dateString}.pdf`;
}

/**
 * Generates a PDF filename for Transaction History export
 * Format: Transaction_History_YYYY-MM-DD-H:MMam/pm.pdf
 */
export function generateTransactionHistoryPdfFilename(): string {
  const dateString = formatDateForFilename();
  return `Transaction_History_${dateString}.pdf`;
}

/**
 * Generates a PDF filename for Inventory Valuation Report export
 * Format: Inventory_Valuation_YYYY-MM-DD-H:MMam/pm.pdf
 */
export function generateInventoryValuationPdfFilename(): string {
  const dateString = formatDateForFilename();
  return `Inventory_Valuation_${dateString}.pdf`;
}

/**
 * Generates a PDF filename for Budget Details export
 * Format: ProjectName_Budget_WithCosts_YYYY-MM-DD-H:MMam/pm.pdf
 * or:     ProjectName_Budget_MaterialsOnly_YYYY-MM-DD-H:MMam/pm.pdf
 */
export function generateBudgetPdfFilename(
  projectName: string,
  includeCosts: boolean,
): string {
  const sanitizedName = sanitizeFilename(projectName);
  const mode = includeCosts ? 'WithCosts' : 'MaterialsOnly';
  const dateString = formatDateForFilename();
  return `${sanitizedName}_Budget_${mode}_${dateString}.pdf`;
}
